import { create } from 'zustand';
import { httpClient } from './http-client';

/** A household's own Plaid keys, without the secret (issue #42). */
export interface PlaidKeys {
  household_id: string;
  client_id: string;
  secret_last4: string;
  environment: 'production' | 'sandbox';
  label: string;
  validated_at?: string;
  updated_at: string;
}

/** GET /plaid/access: what the UI may show. */
export interface PlaidAccess {
  is_admin: boolean;
  plan: string;
  paid: boolean;
  /** The Settings "Bank connections (Plaid)" section is shown (paid plan only). */
  can_add_keys: boolean;
  /** The Connections tab is shown: platform operator, or paid with own keys. */
  connections_enabled: boolean;
  byo?: PlaidKeys | null;
  byo_set?: string;
  paused: boolean;
  pause_note?: string;
  webhook_url?: string;
}

export interface SavePlaidKeys {
  client_id: string;
  secret: string;
  environment: 'production' | 'sandbox';
  label?: string;
}

const unwrap = <T,>(raw: any): T => ((raw && raw.data) || raw) as T;

export const PlaidAccessService = {
  async access(): Promise<PlaidAccess> {
    return unwrap(await httpClient.get<any>('/plaid/access'));
  },
  async save(keys: SavePlaidKeys): Promise<{ credentials: PlaidKeys | null; webhook_url: string }> {
    return unwrap(await httpClient.put<any>('/plaid/byo-credentials', keys));
  },
  async remove(): Promise<void> {
    await httpClient.delete('/plaid/byo-credentials');
  },
};

interface PlaidAccessState {
  access: PlaidAccess | null;
  loaded: boolean;
  refresh: () => Promise<void>;
}

/** Shared so the nav and the settings section agree after a save. */
export const usePlaidAccess = create<PlaidAccessState>((set) => ({
  access: null,
  loaded: false,
  refresh: async () => {
    try {
      set({ access: await PlaidAccessService.access(), loaded: true });
    } catch {
      set({ loaded: true });
    }
  },
}));
